import { useEffect, useMemo, useRef } from 'react'

import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

import { Trades, TradesSchemas } from '@fortem-monorepo/web-sdk'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { UnitMultiplierFormField } from '@/pages/instruments/new-instrument/form-fields/UnitMultiplierFormField'
import { AMCStrategyFormFields } from '@/pages/trading/new-trade/components/AMCStrategyFormFields'
import { BespokeStrategyFormFields } from '@/pages/trading/new-trade/components/BespokeStrategyFormFields'
import BondTradeFormFields from '@/pages/trading/new-trade/components/BondTradeFormFields'
import { CurrencyTradeFormFields } from '@/pages/trading/new-trade/components/CurrencyTradeFormFields'
import { FundStrategyFormFields } from '@/pages/trading/new-trade/components/FundStrategyFormFields'
import { useSetInstrumentData } from '@/pages/trading/new-trade/components/use-set-instrument-data'
import { CounterpartyIdFormField } from '@/pages/trading/new-trade/fields/CounterpartyIdFormField'
import { CurrencyIdFormField } from '@/pages/trading/new-trade/fields/CurrencyIdFormField'
import { DatabaseFormField } from '@/pages/trading/new-trade/fields/DatabaseFormField'
import { InstrumentIdFormField } from '@/pages/trading/new-trade/fields/InstrumentIdFormField'
import { InstrumentTypeIdFormField } from '@/pages/trading/new-trade/fields/InstrumentTypeIdFormField'
import { NotionalFormField } from '@/pages/trading/new-trade/fields/NotionalFormField'
import { PriceFormatFormField } from '@/pages/trading/new-trade/fields/PriceFormatFormField'
import { QuantityFormField } from '@/pages/trading/new-trade/fields/QuantityFormField'
import { SettlementDateFormField } from '@/pages/trading/new-trade/fields/SettlementDateFormField'
import { StrategyIdFormField } from '@/pages/trading/new-trade/fields/StrategyIdFormField'
import { TagsFormField } from '@/pages/trading/new-trade/fields/TagsFormField'
import { TradeDatetimeFormField } from '@/pages/trading/new-trade/fields/TradeDatetimeFormField'
import { TradeInstructionDateFormField } from '@/pages/trading/new-trade/fields/TradeInstructionDateFormField'
import { useCurrencies } from '@/react-query/queries/currencies'
import { useInstrumentTypesQuery, useInstrumentsByTypeQuery } from '@/react-query/queries/instruments'
import { useTradeCounterpartiesQuery, useTradeStrategiesQuery } from '@/react-query/queries/static'

const strategyConfig: {
  [K in Trades.BookTradeBody['strategy_type']]: {
    defaultValues: Partial<Extract<Trades.BookTradeBody, { strategy_type: K }>>
    schema: z.ZodObject
    fields: React.ComponentType
  }
} = {
  FUND: {
    defaultValues: { agent: 'NT' },
    schema: TradesSchemas.fundStrategySchema,
    fields: FundStrategyFormFields,
  },
  AMC: {
    defaultValues: { agent: 'NT' },
    schema: TradesSchemas.amcStrategySchema,
    fields: AMCStrategyFormFields,
  },
  BESPOKE: {
    defaultValues: {},
    schema: TradesSchemas.bespokeStrategySchema,
    fields: BespokeStrategyFormFields,
  },
}

const instrumentTypeFields: Record<number, React.ComponentType> = {
  2: BondTradeFormFields,
  3: CurrencyTradeFormFields,
}

export interface BookTradeFormFieldsV2Props {}

export const BookTradeFormFieldsV2: React.FC<BookTradeFormFieldsV2Props> = ({}) => {
  const { watch, trigger, getValues, reset, setValue, unregister } = useFormContext<Trades.BookTradeBody>()

  const instrumentTypeQuery = useInstrumentTypesQuery()
  const tradeStrategiesQuery = useTradeStrategiesQuery()
  const currenciesQuery = useCurrencies()
  const counterpartiesQuery = useTradeCounterpartiesQuery()

  const instrument_type_id: number | undefined = watch('instrument_type_id')

  const instrumentsQuery = useInstrumentsByTypeQuery([instrument_type_id!])

  const strategy_type = watch('strategy_type')
  const strategy_id = watch('strategy_id')
  const instrument_id = watch('instrument_id')
  const client_id = watch('client_id')

  const previousStrategyType = useRef(strategy_type)

  const selectedInstrumentType = instrumentTypeQuery.data?.find((i) => i.value === instrument_type_id)

  const instrumentDataQuery = useSetInstrumentData({
    instrument_id,
    strategy_id,
  })

  // Get the fields for the instrument type (if exists)
  const InstrumentTypeFields = useMemo(() => {
    if (instrument_type_id === 2 && strategy_type !== 'FUND' && strategy_type !== 'AMC') return undefined
    if (instrument_type_id) {
      return instrumentTypeFields?.[instrument_type_id]
    }
  }, [instrument_type_id, strategy_type])

  // Get the fields for the strategy (if exists)
  const StrategyTypeFields = useMemo(() => {
    if (strategy_type) {
      return strategyConfig?.[strategy_type]?.fields
    }
  }, [strategy_type])

  useEffect(() => {
    trigger()
  }, [strategy_type, instrument_type_id])

  // Feature to remove any fields that aren't needed when changing strategy
  // Also updates with the latest default values for the chosen strategy
  useEffect(() => {
    if (previousStrategyType.current === strategy_type) {
      return
    }

    setValue('trader_id', null)

    // Copy form values
    const newConfig = strategyConfig[strategy_type]
    let _values = Object.assign({}, getValues())

    // Get a list of new default values
    const currentActiveKeys = Object.keys(_values)
    const newPossibleKeys = Object.keys(newConfig?.schema.shape ?? {})

    // Keys that are active now, but aren't in the next schema
    const keysToUnregister = currentActiveKeys.filter((k) => !newPossibleKeys.includes(k)) as (keyof typeof _values)[]

    if (keysToUnregister.length) {
      unregister(keysToUnregister)
    }

    const newValues = newConfig?.defaultValues

    Object.entries(newValues ?? {}).forEach(([key, value]) => {
      setValue(key as any, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      })
    })

    previousStrategyType.current = strategy_type
  }, [strategy_type])

  useEffect(() => {
    setValue('trader_id', null)
  }, [client_id])

  useEffect(() => {
    reset({
      ...getValues(),
      accrued_interests: null,
      // @ts-ignore
      instrument_id: null,
    })
  }, [instrument_type_id])

  return (
    <div className={'@container flex flex-col gap-4'}>
      <FormFieldRow>
        <InstrumentTypeIdFormField instrumentTypes={instrumentTypeQuery.data} />
        <InstrumentIdFormField
          instruments={instrumentsQuery.data}
          loading={instrumentsQuery.isFetching}
          disabled={!instrument_type_id && !instrumentTypeQuery.isFetching}
        />
        <StrategyIdFormField strategies={tradeStrategiesQuery.data} loading={tradeStrategiesQuery.isFetching} />
      </FormFieldRow>

      {InstrumentTypeFields && (
        <div className="flex flex-col gap-4 p-4 rounded-md border bg-gray-100">
          {selectedInstrumentType && (
            <span className={'text-sm text-muted-foreground font-medium'}>{selectedInstrumentType.label} Fields</span>
          )}
          <InstrumentTypeFields />
        </div>
      )}

      <div className="flex flex-col gap-4 p-4 rounded-md border bg-gray-100">
        <FormFieldRow>
          <CurrencyIdFormField
            currencies={currenciesQuery.data}
            loading={currenciesQuery.isFetching || instrumentDataQuery.isFetching}
            disabled={instrumentDataQuery.isFetching}
          />
          <CounterpartyIdFormField counterparties={counterpartiesQuery.data} loading={counterpartiesQuery.isFetching} />
          <TradeDatetimeFormField />
          <SettlementDateFormField />
        </FormFieldRow>
        <FormFieldRow>
          <PriceFormatFormField />
          <QuantityFormField />
          <NotionalFormField />
          <UnitMultiplierFormField disabled={true} />
        </FormFieldRow>
        <FormFieldRow>
          <TagsFormField />
          <TradeInstructionDateFormField />
        </FormFieldRow>
      </div>

      {StrategyTypeFields && (
        <div className="flex flex-col gap-4 p-4 rounded-md border bg-gray-100">
          {strategy_type && (
            <span className={'text-sm text-muted-foreground font-medium'}>{strategy_type} Strategy Fields</span>
          )}
          <StrategyTypeFields />
        </div>
      )}

      {strategy_type && strategy_type !== 'BESPOKE' && (
        <div className="p-4 rounded-md border bg-gray-100">
          <DatabaseFormField />
        </div>
      )}
    </div>
  )
}
